import { propertyType } from '@/components/core/async-data-table/type';
import { useProductVariants, useSearchProducts } from '@/services/product-service';
import {
  FilterRequest,
  SearchProp,
  SortConfig,
  VariantStockSearchItem,
} from '@/types/product-type';
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

interface ProductStats {
  totalProducts: number;
  activeProducts: number;
  draftProducts: number;
}

interface ProductContextValue {
  // Variants data (flat list from API)
  variants: VariantStockSearchItem[];
  isLoading: boolean;
  error: Error | null;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalElements: number;
    isEmpty: boolean;
    isFirst: boolean;
    isLast: boolean;
  };
  stats: ProductStats;

  // Pagination controls
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  stockStatus: string;
  setStockStatus: (status: string) => void;
  sortConfig: SortConfig[];
  setSortConfig: (sorts: SortConfig[]) => void;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;

  // Generic property search (for FilterHeader pattern)
  propSearch: propertyType[];
  setPropSearch: (props: propertyType[]) => void;

  // Refresh
  refresh: () => void;

  // Column sorting
  handleColumnSort: (field: string) => void;
}

const ProductContext = createContext<ProductContextValue | undefined>(undefined);

export function ProductProvider({ children }: { children: ReactNode }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [stockStatus, setStockStatus] = useState('all');
  const [sortConfig, setSortConfig] = useState<SortConfig[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [propSearch, setPropSearch] = useState<propertyType[]>([]);

  // Build filter request matching backend format
  const filterRequest: FilterRequest | null = useMemo(() => {
    const searchProps: SearchProp[] = [];

    // Add active/inactive filter
    if (activeTab === 'active') {
      searchProps.push({
        propName: 'p.is_active',
        value: 'true',
      });
    } else if (activeTab === 'inactive') {
      searchProps.push({
        propName: 'p.is_active',
        value: 'false',
      });
    }

    // Add stock status filter
    if (stockStatus === 'in_stock') {
      searchProps.push({
        propName: 's.stock_in_hand',
        value: '>0',
      });
    } else if (stockStatus === 'out_of_stock') {
      searchProps.push({
        propName: 's.stock_in_hand',
        value: '<=0',
      });
    }

    // Merge generic propSearch values (from FilterHeader pattern)
    for (const prop of propSearch) {
      if (prop.value && prop.value.toString().trim() !== '') {
        searchProps.push({
          propName: prop.propName,
          value: prop.value.toString(),
        });
      }
    }

    // Determine sortBy and ordinal from sortConfig
    // Default to p.name ascending if no sort config
    let sortBy = 'p.name';
    let ordinal: 'asc' | 'desc' = 'asc';

    if (sortConfig.length > 0) {
      const firstSort = sortConfig[0];
      sortBy = firstSort.field;
      ordinal = firstSort.direction;
    }

    // Normalize search query for case-insensitive search
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return {
      filterText: normalizedQuery || null,
      searchProp: searchProps.length > 0 ? searchProps : null,
      page,
      size: pageSize,
      sortBy,
      ordinal,
    };
  }, [searchQuery, activeTab, stockStatus, sortConfig, page, pageSize, propSearch]);

  // Fetch variants (flat list from API)
  const {
    items: variants,
    isLoading,
    error,
    totalElements,
    totalPages,
    isEmpty,
    isFirst,
    isLast,
    currentPage,
    refresh,
  } = useSearchProducts(filterRequest);

  // Calculate stats
  const stats = useMemo(() => {
    const totalProducts = totalElements;
    // Note: We can't calculate active/draft from ProductResponse alone
    // This would need to come from backend or be calculated differently
    const activeProducts = 0;
    const draftProducts = 0;

    return {
      totalProducts,
      activeProducts,
      draftProducts,
    };
  }, [totalElements]);

  // Handle column sort - cycles through: not sorted -> asc -> desc -> not sorted
  const handleColumnSort = useCallback((field: string) => {
    setSortConfig((prev) => {
      const existingIndex = prev.findIndex((sort) => sort.field === field);
      const newSorts = [...prev];

      if (existingIndex >= 0) {
        const existingSort = newSorts[existingIndex];
        if (existingSort.direction === 'asc') {
          // Change to desc
          newSorts[existingIndex] = { field, direction: 'desc' };
          return newSorts;
        } else {
          // Remove sort
          newSorts.splice(existingIndex, 1);
          return newSorts;
        }
      } else {
        // Add new sort with asc direction
        return [...newSorts, { field, direction: 'asc' as const }];
      }
    });
    // Reset to first page when sorting changes
    setPage(0);
  }, []);

  const value: ProductContextValue = useMemo(
    () => ({
      variants,
      isLoading,
      error: error || null,
      pagination: {
        currentPage,
        pageSize,
        totalPages,
        totalElements,
        isEmpty,
        isFirst,
        isLast,
      },
      stats,
      searchQuery,
      setSearchQuery,
      activeTab,
      setActiveTab,
      stockStatus,
      setStockStatus,
      sortConfig,
      setSortConfig,
      page,
      setPage,
      pageSize,
      setPageSize,
      propSearch,
      setPropSearch,
      refresh,
      handleColumnSort,
    }),
    [
      variants,
      isLoading,
      error,
      currentPage,
      pageSize,
      totalPages,
      totalElements,
      isEmpty,
      isFirst,
      isLast,
      stats,
      searchQuery,
      activeTab,
      stockStatus,
      sortConfig,
      page,
      propSearch,
      refresh,
      handleColumnSort,
    ],
  );

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
}

export function useProductContext() {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error('useProductContext must be used within a ProductProvider');
  }
  return context;
}

// Hook for fetching variants for a specific product
// Only fetches when productId is provided and enabled is true (for modals)
export function useProductVariantsData(productId: string | null, enabled: boolean = true) {
  // Fetch if enabled and productId is provided
  const shouldFetch = enabled && productId !== null;

  const { data: variants, isLoading } = useProductVariants(productId, shouldFetch);

  return { variants: variants || [], isLoading };
}
